import { memo, type FC } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { KanbanTicketView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import {
  formatTicketDueDate,
  ticketDueColor,
  ticketDueUrgency,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { TicketCurativeContext } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/TicketCurativeContext/TicketCurativeContext';
import {
  WORK_ORDER_UI,
  softBadgeStyle,
  ticketStatusTone,
  executionResultTone,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { TicketChipDragHandle, TicketDraggableShell } from './ticket-chip-dnd';

export interface WorkOrderTicketChipProps {
  ticket: KanbanTicketView;
  workOrderId: string;
  organizationId: string;
  disabled?: boolean;
  /**
   * When false, chip is visual-only (no useDraggable). Used to unmount DnD
   * subscriptions for inactive chips during an opposite / sibling drag.
   */
  mountDraggable?: boolean;
  onOpenMoveTicket?: (ticketId: string) => void;
}

type ChipChromeProps = {
  ticket: KanbanTicketView;
  organizationId: string;
  disabled: boolean;
  showHandle: boolean;
  onOpenMoveTicket?: (ticketId: string) => void;
};

const WorkOrderTicketChipChrome = memo(function WorkOrderTicketChipChrome({
  ticket,
  organizationId,
  disabled,
  showHandle,
  onOpenMoveTicket,
}: ChipChromeProps) {
  const { t } = useTranslation('maintenance');
  const statusTone = ticketStatusTone(ticket.status);
  const dueLabel = formatTicketDueDate(ticket.dueAt);
  const dueUrgency = ticketDueUrgency(ticket.dueAt);
  const lastResult = ticket.lastExecutionResult;

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
        borderLeft: `3px solid ${statusTone.accent}`,
        borderRadius: 8,
        padding: '6px 8px',
        background: '#f8fafc',
      }}
    >
      {showHandle ? <TicketChipDragHandle /> : null}

      <div style={{ minWidth: 0, flex: 1 }}>
        <Space size={4} wrap>
          <Typography.Text strong style={{ fontSize: 12 }}>
            {ticket.code}
          </Typography.Text>
          <Tag
            style={{
              margin: 0,
              fontSize: 11,
              lineHeight: '18px',
              ...softBadgeStyle(statusTone),
              fontWeight: 700,
            }}
          >
            {t(`workOrders.ticketStatus.${ticket.status}`, {
              defaultValue: ticket.status,
            })}
          </Tag>
          <Tag
            style={{
              margin: 0,
              ...softBadgeStyle(
                WORK_ORDER_UI.ticketType[
                  ticket.type as keyof typeof WORK_ORDER_UI.ticketType
                ] ?? WORK_ORDER_UI.ticketType.PREVENTIVE,
              ),
            }}
          >
            {t(`workOrders.ticketType.${ticket.type}`)}
          </Tag>
          <Tag
            style={{
              margin: 0,
              ...softBadgeStyle(
                WORK_ORDER_UI.priority[
                  ticket.priority as keyof typeof WORK_ORDER_UI.priority
                ] ?? WORK_ORDER_UI.priority.MEDIUM,
              ),
            }}
          >
            {t(`workOrders.priority.${ticket.priority}`)}
          </Tag>
          {lastResult ? (
            <Tag
              style={{
                margin: 0,
                fontWeight: 700,
                ...softBadgeStyle(executionResultTone(lastResult)),
              }}
              title={t('workOrders.kanban.lastResultHint')}
            >
              {t('workOrders.kanban.lastResult', {
                result: t(`workOrders.executionResult.${lastResult}`, {
                  defaultValue: lastResult,
                }),
              })}
            </Tag>
          ) : null}
          {ticket.needsFollowUp ? (
            <Tag color="gold" style={{ margin: 0, fontWeight: 600 }}>
              {t('workOrders.kanban.needsFollowUp')}
            </Tag>
          ) : null}
          {ticket.needsFollowUp && onOpenMoveTicket && !disabled ? (
            <Typography.Link
              onClick={(event) => {
                event.stopPropagation();
                onOpenMoveTicket(ticket.id);
              }}
              style={{ fontSize: 12 }}
            >
              {t('workOrders.kanban.moveTicket')}
            </Typography.Link>
          ) : null}
        </Space>

        {dueLabel && dueUrgency ? (
          <Typography.Text
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 6,
              fontSize: 11,
              fontWeight: dueUrgency === 'normal' ? 600 : 700,
              color: ticketDueColor(dueUrgency),
            }}
          >
            <CalendarOutlined />
            {dueUrgency === 'overdue'
              ? t('workOrders.kanban.dueOverdue', { date: dueLabel })
              : dueUrgency === 'soon'
                ? t('workOrders.kanban.dueSoon', { date: dueLabel })
                : t('workOrders.kanban.dueLabel', { date: dueLabel })}
          </Typography.Text>
        ) : null}

        <TicketCurativeContext
          type={ticket.type}
          planName={ticket.planName}
          planCode={ticket.planCode}
          ticketId={ticket.id}
          ticketTitle={ticket.title}
          organizationId={organizationId || undefined}
          incidentId={ticket.incidentId}
          incidentCode={ticket.incidentCode}
          incidentTitle={ticket.incidentTitle}
          summaryMaxRows={2}
        />

        {ticket.assetName || ticket.assetSerialNumber ? (
          <Typography.Text
            style={{
              display: 'block',
              marginTop: 4,
              fontSize: 11,
              color: WORK_ORDER_UI.sidebarNavy,
            }}
          >
            {t('workOrders.kanban.assetLabel', {
              name: ticket.assetName ?? '—',
              serial: ticket.assetSerialNumber ?? '—',
            })}
          </Typography.Text>
        ) : null}

        <Typography.Paragraph
          type="secondary"
          ellipsis={{ rows: 1 }}
          style={{ margin: '4px 0 0', fontSize: 12 }}
        >
          {ticket.title}
        </Typography.Paragraph>
      </div>
    </div>
  );
});

export const WorkOrderTicketChip: FC<WorkOrderTicketChipProps> = memo(
  function WorkOrderTicketChip({
    ticket,
    workOrderId,
    organizationId,
    disabled = false,
    mountDraggable = !disabled,
    onOpenMoveTicket,
  }) {
    const chrome = (
      <WorkOrderTicketChipChrome
        ticket={ticket}
        organizationId={organizationId}
        disabled={disabled}
        showHandle={!disabled}
        onOpenMoveTicket={onOpenMoveTicket}
      />
    );

    if (!mountDraggable) {
      return chrome;
    }

    return (
      <TicketDraggableShell
        ticketId={ticket.id}
        ticketCode={ticket.code}
        workOrderId={workOrderId}
      >
        {chrome}
      </TicketDraggableShell>
    );
  },
);
